import { Box, VStack } from "@chakra-ui/react";
import { Field, FormCheckbox, FormInput, FormSelect, FormTextarea } from "@/components/jobOffer/ui";
import { SectionCard, SectionHeader, Grid2 } from "@/components/jobOffer/ui/layout";
import { AddButton, ItemHeader } from "@/components/jobOffer/ui/listHelpers";
import { CHANNEL_OPTIONS } from "@/components/campaign/channelOptions";

const DELAY_PRESETS = [
    { value: "0", label: "Natychmiast" },
    { value: "240", label: "Po 4 godzinach" },
    { value: "1440", label: "Po 24 godzinach (następnego dnia)" },
    { value: "4320", label: "Po 3 dniach" },
    { value: "10080", label: "Po tygodniu" },
];
const DELAY_OPTIONS = [...DELAY_PRESETS, { value: "custom", label: "Inne (podaj liczbę minut)" }];
const PRESET_VALUES = new Set(DELAY_PRESETS.map(p => p.value));

const STOP_CONDITION_OPTIONS = [
    { value: "LeadResponded", label: "Lead odpowiedział na wiadomość" },
    { value: "LeadConverted", label: "Lead dokonał zakupu/konwersji" },
    { value: "MeetingBooked", label: "Spotkanie zostało umówione" },
    { value: "StatusChangedManually", label: "Status leada zmienił się ręcznie" },
    { value: "LeadRequestedNoContact", label: "Lead poprosił o brak kontaktu" },
];

const newAttempt = () => ({ id: crypto.randomUUID(), delayMinutes: 0, channel: null, messageTemplate: "" });

const StepFollowUp = ({ values, onChange }) => {
    const set = (key, val) => onChange({ ...values, [key]: val });

    const followUp = values.followUpConfig ?? { attempts: [], stopConditions: [] };
    const setFollowUp = (patch) => set("followUpConfig", { ...followUp, ...patch });

    const attempts = followUp.attempts ?? [];
    const setAttempts = (next) => setFollowUp({ attempts: next });
    const updateAttempt = (i, patch) => setAttempts(attempts.map((a, idx) => idx === i ? { ...a, ...patch } : a));
    const removeAttempt = (i) => setAttempts(attempts.filter((_, idx) => idx !== i));
    const addAttempt = () => setAttempts([...attempts, newAttempt()]);

    const stopConditions = followUp.stopConditions ?? [];
    const toggleStopCondition = (value, checked) => setFollowUp({
        stopConditions: checked ? [...stopConditions, value] : stopConditions.filter(c => c !== value),
    });

    return (
        <VStack w="full" align="stretch" gap={4}>
            <SectionCard>
                <SectionHeader number={15} title="Strategia prób kontaktu" subtitle="Kolejne próby follow-up, jeśli lead nie odpowiedział" />
                <VStack align="stretch" gap={4}>
                    {attempts.map((a, i) => {
                        const delayValue = String(a.delayMinutes ?? 0);
                        const isPreset = PRESET_VALUES.has(delayValue);
                        return (
                            <Box key={a.id ?? i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
                                <ItemHeader title={`Próba ${i + 1}`} index={i} onRemove={() => removeAttempt(i)} />
                                <VStack align="stretch" gap={3}>
                                    <Grid2>
                                        <Field label="Opóźnienie">
                                            <FormSelect
                                                value={isPreset ? delayValue : "custom"}
                                                onChange={(v) => updateAttempt(i, { delayMinutes: v === "custom" ? a.delayMinutes : Number(v) })}
                                                options={DELAY_OPTIONS}
                                                placeholder="Wybierz opóźnienie"
                                            />
                                        </Field>
                                        <Field label="Kanał">
                                            <FormSelect
                                                value={a.channel ?? ""}
                                                onChange={(v) => updateAttempt(i, { channel: v || null })}
                                                options={CHANNEL_OPTIONS}
                                                placeholder="Wybierz kanał"
                                            />
                                        </Field>
                                    </Grid2>
                                    {!isPreset && (
                                        <Field label="Liczba minut">
                                            <FormInput
                                                type="number" min="0"
                                                value={a.delayMinutes ?? 0}
                                                onChange={(e) => updateAttempt(i, { delayMinutes: e.target.value === "" ? 0 : Number(e.target.value) })}
                                            />
                                        </Field>
                                    )}
                                    <Field label="Wiadomość follow-up (opcjonalnie)">
                                        <FormTextarea value={a.messageTemplate ?? ""} onChange={(e) => updateAttempt(i, { messageTemplate: e.target.value })} rows={2} />
                                    </Field>
                                </VStack>
                            </Box>
                        );
                    })}
                    <AddButton label="Dodaj próbę follow-up" onClick={addAttempt} />
                </VStack>
            </SectionCard>

            <SectionCard>
                <SectionHeader number={16} title="Warunki zatrzymania follow-up" subtitle="Kolejne próby zostaną automatycznie zatrzymane, jeśli:" />
                <VStack align="stretch" gap={3}>
                    {STOP_CONDITION_OPTIONS.map((opt) => (
                        <FormCheckbox
                            key={opt.value}
                            label={opt.label}
                            checked={stopConditions.includes(opt.value)}
                            onChange={(checked) => toggleStopCondition(opt.value, checked)}
                        />
                    ))}
                </VStack>
            </SectionCard>
        </VStack>
    );
};

export default StepFollowUp;
